import { FieldNumberFormat } from 'components/FieldNumberFormat';
import { TextWithTooltip } from 'components/WithTooltip';
import type { BimContractOtherStatusEnum } from 'gql/graphql';
import { SectionEnum, DeviationTipEnum, OtherCostCalculationTypeEnum } from 'gql/graphql';
import { CodeCell } from 'pages/Project/Common/CodeCell';
import { TYPE_CONST } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/types';
import type { TableColumn } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type { RefObject } from 'react';

import Styles from './BimAddOtherStyles.module.css';

const OTHER_TABLE_GROUP_NAME = {
    [SectionEnum.CustomerLimits]: 'Лимиты Заказчика',
    [SectionEnum.Others]: 'Глава 9',
    [SectionEnum.WorkOrderTotals]: '',
};

interface IGetBaseColumnsByContractType {
    hideIncludeField?: boolean;
    valueSetter: (id: number | string) => ({ value }: any) => void;
    checkedIds: {
        [key: number | string]: {
            [key: number | string]: any;
        };
    };
    tableRef: RefObject<HTMLDivElement>;
    isWorkOrderChangePage?: boolean;
}

type TBaseDataFields = {
    name: string;
    calculatingType: OtherCostCalculationTypeEnum;
    costLimit: number;
    deviation: DeviationTipEnum;
    status: BimContractOtherStatusEnum;
    section?: SectionEnum | null;
    totalRemainderCost?: number;
    calculatingTypeValue?: number;
};

type TGetBaseColumnsByContractType = { id: string; nestLevel?: number; parentId?: number; objectId?: number };

const defineBalance = (deviation: DeviationTipEnum) => {
    if (deviation === DeviationTipEnum.Negative) {
        return {
            color: '#EB3333',
        };
    }

    return deviation === DeviationTipEnum.LessThan30
        ? { color: '#EB6D00', tooltip: 'Остаток по прочим затратам меньше 30%' }
        : {
              color: '#000',
          };
};

const onNameClick = (id: string, tableRef: RefObject<HTMLDivElement>) => () => {
    tableRef.current?.querySelector<HTMLLabelElement>(`.menu-checkbox-${id}`)?.click();
};

/*
 * TODO разобраться с типизацией. что-то не так в типе TableColumn,
 *  но все типы выводятся правильно при использовании
 */
export const getBaseColumnsByContractType = <T extends TBaseDataFields>({
    hideIncludeField = false,
    valueSetter,
    checkedIds,
    tableRef,
    isWorkOrderChangePage = false,
}: IGetBaseColumnsByContractType): TableColumn<T & TGetBaseColumnsByContractType>[] => [
    {
        // @ts-ignore
        title: 'Наименование',
        accessor: 'name',
        width: 350,
        renderCell: (row) => (
            <div className={Styles.verticalCenteredCell}>
                {row.status && <CodeCell status={row.status} />}
                <span role="none" onClick={onNameClick(row.id, tableRef)}>
                    {row.name}
                </span>
            </div>
        ),
    },
    {
        // @ts-ignore
        title: 'Раздел',
        accessor: 'section',
        width: 200,
        renderCell: (row) =>
            row.section && OTHER_TABLE_GROUP_NAME[row.section as Exclude<SectionEnum, SectionEnum.LimitedCost>],
    },
    {
        // @ts-ignore
        title: 'Способ расчета',
        accessor: 'calculatingType',
        width: 100,
        renderCell: (row) =>
            (row.calculatingType && TYPE_CONST[row.calculatingType]) ||
            (row.calculatingType && `${formatNumberToRu(row.calculatingTypeValue, 3)} %`),
    },
    {
        // @ts-ignore
        title: 'Лимит по Договору',
        accessor: 'costLimit',
        width: hideIncludeField ? 180 : 130,
        renderCell: (row) => formatNumberToRu(row.costLimit, 0),
    },
    {
        // @ts-ignore
        title: 'Остаток по Договору',
        accessor: 'totalRemainderCost',
        width: hideIncludeField ? 150 : 130,
        renderCell: (row) => {
            const { color, tooltip } = defineBalance(row.deviation);
            const labelText = formatNumberToRu(row.totalRemainderCost, 0);

            return (
                <div>
                    {tooltip ? (
                        <TextWithTooltip tooltipText={tooltip} text={labelText} tooltipColor={color} />
                    ) : (
                        <div style={{ color }}>{labelText}</div>
                    )}
                </div>
            );
        },
    },
    {
        // @ts-ignore
        title: isWorkOrderChangePage ? 'Включить в ИЗМ. к НЗ' : 'Включить в НЗ',
        accessor: 'deviation',
        width: 110,
        hidden: hideIncludeField,
        renderCell: (row) => {
            if (row.nestLevel !== 0 || !(row.calculatingType === OtherCostCalculationTypeEnum.Calculation)) {
                return '';
            }

            const withArrowUp =
                !!checkedIds[row.id]?.parent &&
                row.totalRemainderCost &&
                row.totalRemainderCost < (replacerCost(checkedIds[row.id]?.value) ?? 0);

            return (
                <FieldNumberFormat
                    value={checkedIds[row.id]?.value ?? '0'}
                    decimalScale={0}
                    size="xs"
                    onChange={valueSetter(row.id)}
                    disabled={!checkedIds[row.id]?.parent}
                    leftSide={withArrowUp ? '▲' : undefined}
                    className={withArrowUp ? Styles.negativeValue : ''}
                />
            );
        },
    },
];
